/**
 * Opening Claim detail already narrowed to a person or a team — the Ledger's "Claims"
 * buttons beside each "Touches" one (person drawer, Me hero, supervisor drawer,
 * Overview slice). The twin of touchDrill.ts.
 *
 * The selection travels in router state, not the URL: a URL carrying staff names
 * ends up in browser history, proxy logs and pasted links.
 *
 *   const openClaims = useOpenClaimDetail()
 *   openClaims({ employees: [p.employee], label: p.employee })
 *   openClaims({ employees: team.map((p) => p.employee), label: `team of ${sup}` })
 *   openClaims({ employees: slice, label: facility, pick: { f: facility } })
 *
 * One person selects them in the Employee dropdown, as hers does; more than one
 * becomes a removable "team of …" tag on the count line.
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { VIEW_KEY } from '@/state/sharedView'

export interface ClaimDrill {
  employees: string[]
  /** What the tag on the count line says, e.g. "team of Jane Doe". */
  label: string
  /**
   * An Overview slice the claim rows carry — facility, state or payer type — set in
   * Claim detail's own dropdown rather than as people, matched blank-for-blank so an
   * "Unknown" bar finds every blank spelling (data/blankValue.ts). Facility group
   * (`fg`, by name) goes in its Facility group dropdown. Department (`dept`, the claim's,
   * or `wd`, the worker's home department) has no dropdown and becomes a removable tag
   * on the count line instead. If the value is not in the
   * rows, `employees` is used after all.
   */
  pick?: Partial<Record<ClaimPickKey, string>>
  /**
   * Bill months, in place of the top bar's range: a person-page cell's month, or the
   * window a collection rate reads (review #227). A removable tag on the count line.
   */
  months?: string[]
  /**
   * Resolution months, in place of any bill month (review #397): the resolved basis —
   * resolved claims, 0% touch share, touches per touched claim — counts a claim in the
   * month it resolved, billed any month. A removable "Resolved in …" tag; with it the
   * top bar's range no longer limits the bill date.
   */
  resolvedIn?: string[]
  /** A claim status the measure counts, set in the Status dropdown — 'Resolved'. */
  status?: string
  /**
   * Exactly these claims, by facility + visit: Touch drivers' looped, stuck and touched
   * claims (KANB-2236). Overrides the people, months and status; a removable tag on the
   * count line, which says how many of them Claim detail does not carry.
   */
  visits?: { f: string; v: string }[]
}

export type ClaimPickKey = 'fg' | 'f' | 's' | 'p' | 'dept' | 'wd'

export function useOpenClaimDetail(): (drill: ClaimDrill) => void {
  const navigate = useNavigate()
  return useCallback(
    (drill: ClaimDrill) => navigate('/claim-detail', { state: { claimDrill: drill } }),
    [navigate],
  )
}

/**
 * Opening Claim detail on one visit, as the header Search does (#604): its search set to
 * the visit, in the history entry (free text never goes in the URL), and `reveal` so any
 * filter hiding it is set aside. A visit Claim detail does not carry finds nothing there.
 */
export function useOpenClaimVisit(): (visit: string) => void {
  const navigate = useNavigate()
  return useCallback(
    (visit: string) => navigate('/claim-detail', { state: { [VIEW_KEY]: { q: visit.toLowerCase(), reveal: visit } } }),
    [navigate],
  )
}

/** The drill a navigation carried, if any. */
export function readClaimDrill(state: unknown): ClaimDrill | null {
  const d = (state as { claimDrill?: ClaimDrill } | null)?.claimDrill
  return d && Array.isArray(d.employees) && typeof d.label === 'string' ? d : null
}
